import { Text as T } from '@eo-locale/preact';
import { lazy, Suspense } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { ExtendedReviewsWidgetSettings } from 'types/settings';
import { useImage } from 'utilities/content';

import { Image } from 'components/Image/Image';
import { useSettings } from 'components/SettingsProvider';

import { useImageGrid } from './utilities';

import styles from './ImageGrid.module.scss';

const ReviewModal = lazy(() => import('components/ReviewModal'));

const INITIAL_IMAGES_COUNT = 7;

export function ImageGrid() {
  const [showAll, setShowAll] = useState(false);
  const { images, reviews } = useImageGrid(showAll);
  const { enable_show_full_review, show_location } =
    useSettings<ExtendedReviewsWidgetSettings>();

  const [slide, setSlide] = useState(0);
  const [currentImage, setCurrentImage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const visibleItems = !showAll
    ? images.slice(0, INITIAL_IMAGES_COUNT)
    : images;
  const currentReview = reviews[slide];
  const { getImageUrl } = useImage();

  const handleClickMore = useCallback(() => setShowAll(true), []);

  const handleImageClick = useCallback((id: number, imageKey: string) => {
    setSlide(id);
    setCurrentImage(imageKey);
    setModalOpen(true);
  }, []);

  const handleNext = useCallback(() => {
    const index = images.findIndex(([imageKey]) => imageKey === currentImage);

    if (index === -1) {
      return;
    }

    let nextImage = images[index + 1];

    if (!nextImage) {
      nextImage = images[0];
    }

    const [imageKey, id] = nextImage;

    setSlide(id);
    setCurrentImage(imageKey);
  }, [images, currentImage]);

  const handlePrev = useCallback(() => {
    const index = images.findIndex(([imageKey]) => imageKey === currentImage);

    if (index === -1) {
      return;
    }

    let prevImage = images[index - 1];

    if (!prevImage) {
      prevImage = images[images.length - 1];
    }

    const [imageKey, id] = prevImage;

    setSlide(id);
    setCurrentImage(imageKey);
  }, [images, currentImage]);

  useEffect(() => {
    function keyboardListener(event: KeyboardEvent) {
      if (event.key === 'ArrowRight') handleNext();
      if (event.key === 'ArrowLeft') handlePrev();
    }

    window.addEventListener('keydown', keyboardListener);

    return () => {
      window.removeEventListener('keydown', keyboardListener);
    };
  }, [handleNext, handlePrev]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const itemsMarkup = visibleItems.map(([imageKey, reviewId], index) => {
    const handleClick = () => {
      handleImageClick(reviewId, imageKey);
    };

    const source = getImageUrl(imageKey, index === 0 ? '800' : '400');
    const fallback = getImageUrl(imageKey);

    return (
      <div key={index} className={styles.Item}>
        <div className={styles.Image}>
          <Image fallback={fallback} source={source} alt="" />
        </div>
        {images.length > INITIAL_IMAGES_COUNT &&
        !showAll &&
        index === INITIAL_IMAGES_COUNT - 1 ? (
          <button
            aria-haspopup="dialog"
            className={styles.ShowMore}
            onClick={handleClick}
          >
            <span className={styles.ShowMoreText}>
              <T id="media_list_show_more_button" defaultMessage="Show more" />
            </span>
          </button>
        ) : (
          <button
            aria-haspopup="dialog"
            className={styles.Toggle}
            onClick={handleClick}
          >
            <span className={styles.ToggleText}>
              <T
                id="media_list_open_media_in_gallery_message"
                count={index + 1}
                defaultMessage="Open media {count} in gallery view"
              />
            </span>
          </button>
        )}
      </div>
    );
  });

  const review = { ...currentReview, images: [{ image_key: currentImage }] };
  const reviewModalMarkup =
    slide !== 0 && modalOpen ? (
      <ReviewModal
        open
        contentExpanded={!enable_show_full_review}
        review={review}
        settings={{
          show_location,
          show_thumbnail: true,
        }}
        onClose={handleModalClose}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    ) : null;

  return (
    <>
      <div ref={gridRef} className={styles.ImageGrid}>
        {itemsMarkup}
      </div>
      <Suspense fallback={null}>{reviewModalMarkup}</Suspense>
    </>
  );
}
